import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { Suspense, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { TransactionOrderType } from "creso-core/constants";
import { isDev } from "@/utils/common";
import { QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { ProposalUIState } from "@planning/providers/proposal-provider";
import { GoalUIState } from "@planning/providers/goal-context-provider";
import { AddClientDialogActions } from "@crm/components/onboarding/add-client-dialog";
import { TransactionDialogActions } from "@transactions/components/transaction-dialog";
import { MarketingResourceType } from "creso-core/types";
import { scheduleMobileAppRedirect } from "@/utils/mobile-app-redirect";
import { ErrorComponent } from "web-core";

const Loading = () => {
  return (
    <div className="flex flex-col items-center justify-center size-full">
      <Loader2 className="size-10 animate-spin" />
    </div>
  );
};

const RootComponent = () => {
  const location = useLocation();

  useEffect(() => {
    return scheduleMobileAppRedirect(location.pathname);
  }, [location.pathname]);

  return (
    <>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
      {isDev() && <TanStackRouterDevtools position="bottom-right" />}
    </>
  );
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    component: RootComponent,
    validateSearch: (search) => {
      const query: {
        modal?:
          | "transaction"
          | "client-lead"
          | "mandate"
          | "onboarding"
          | "transaction-list"
          | "proposal"
          | "capital-gains"
          | "portfolio-valuation"
          | "import-holdings"
          | "sip-details"
          | "stp-details"
          | "swp-details"
          | "change-of-broker"
          | "account-updates"
          | "goals"
          | "goal-proposals"
          | "order-detail"
          | "neft-rtgs"
          | "bulk-upload-clients"
          | "create-family"
          | "modify-family"
          | "risk-profile"
          | "order-history"
          | "confirm-joint-account"
          | "add-second-holder";
        context?: string;
        client_id?: number;
        family_id?: number;
        type?: TransactionOrderType;
        category?: MarketingResourceType;
        isin?: string;
        action?: TransactionDialogActions | AddClientDialogActions;
        step?: number;
        folio_id?: number;
        mandate_id?: number;
        order_id?: string;
        collection_id?: number;
        collection_type?: string;
        collection_target?: TransactionOrderType[];
        token?: string;
        proposal_id?: number;
        goal_id?: number;
        state?: ProposalUIState | GoalUIState;
      } = {};

      if (search.modal) {
        query.modal = search.modal as (typeof query)["modal"];
        if (search.modal === "transaction" && !search.isin) {
          query.action = (search.action ||
            "create") as TransactionDialogActions;
        }
        if (search.modal === "add-client") {
          query.action = (search.action || "init") as AddClientDialogActions;
        }
      }

      if (search.modal === "onboarding") {
        query.step = search.step as number;
      }

      if (search.context) {
        query.context = search.context as string;
      }

      if (search.type) {
        query.type = search.type as TransactionOrderType;
      }

      if (search.isin) {
        query.isin = search.isin as string;
      }

      if (search.client_id) {
        query.client_id = search.client_id as number;
      }

      if (search.family_id) {
        query.family_id = search.family_id as number;
      }

      if (search.folio_id) {
        query.folio_id = search.folio_id as number;
      }

      if (search.mandate_id) {
        query.mandate_id = search.mandate_id as number;
      }

      if (search.order_id) {
        query.order_id = search.order_id as string;
      }

      if (search.collection_id) {
        query.collection_id = search.collection_id as number;
      }

      if (search.collection_type) {
        query.collection_type = search.collection_type as string;
      }

      if (search.token) {
        query.token = search.token as string;
      }

      if (search.proposal_id) {
        query.proposal_id = search.proposal_id as number;
      }

      if (search.goal_id) {
        query.goal_id = search.goal_id as number;
      }

      if (search.state) {
        query.state = search.state as ProposalUIState | GoalUIState;
      }

      return query;
    },
    errorComponent: ({ error }) => (
      <ErrorComponent
        error={error}
        onError={(e) => Sentry.captureException(e)}
      />
    ),
  }
);
